import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Select, MenuItem, Slider } from '@material-ui/core';
import { VolumeUp } from '@material-ui/icons';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import messagesToast from '~/validations/messagesNotifications';
import * as lib from '~/dependencies';
import {
  log,
  listRealAudioDevices,
  getSystemDefaultDevice,
  toDeviceInfo,
  stripDefaultPrefix,
  matchPinnedDevice,
  isVirtualDeviceId,
  isPinInUse,
} from '~/helpers';

import { BoxDevices, ListDevices, BoxSelect, VolumeComponent } from './styles';
import { SYSTEM_DEFAULT } from './constants';

export default function DeviceManager({
  disableRingDevice,
  disableAudioTest,
  showSpeakerVolume,
}) {
  // SELECTORS
  const phoneDevice = useSelector(state => state?.system?.deviceManager);
  const pins = useSelector(state => state?.system?.userPreferences?.audio);
  const ringDevice = useSelector(state => state?.system?.ringDevice);
  const volume = useSelector(state => state.system?.volume);

  // STATES
  const [microphones, setMicrophones] = useState([]);
  const [audio, setAudio] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  // The raw enumeration. `microphones`/`speakers` lose `kind` on their way through toDeviceInfo,
  // and the shared device matcher needs it.
  const [allDevices, setAllDevices] = useState([]);
  // What the OS itself currently considers default, per role. Read from the live enumeration and
  // NOT from `deviceManager`, which holds the device in use - those are the same thing only while
  // nothing is pinned. See systemDefaultLabel.
  const [osDefaults, setOsDefaults] = useState({});
  const [volumeValue, setVolumeValue] = useState(volume || 0.5);

  // MEMOs
  // The microphone the picker is showing as selected: the pinned device while it is the one in
  // use, otherwise whatever the OS currently considers default. Resolved from THIS component's
  // own enumeration, which is re-read on every devicechange, rather than from `deviceManager` -
  // so the level meter follows the selection as soon as the hardware moves, without waiting for
  // the resolver thunk to run and rewrite redux.
  const selectedMicrophone = useMemo(() => {
    if (isPinInUse(pins?.microphone, phoneDevice?.audio?.microphone)) {
      return pins.microphone;
    }

    const aggregate = osDefaults.microphone;

    // Firefox and Safari expose no aggregate - what they call the default IS a real device.
    if (!aggregate || !isVirtualDeviceId(aggregate.deviceId)) {
      return aggregate;
    }

    // Resolve the aggregate down to the PHYSICAL device behind it, and preview that by its own
    // id. Two reasons, and both are why the meter kept reading the old microphone:
    //
    //   - 'default' is the same deviceId whatever it points at, so the effect below had nothing
    //     to react to when the OS default moved;
    //   - requesting 'default' asks the browser to resolve it a second time, and that indirection
    //     is exactly what does not reliably follow the move here. A real id cannot be resolved to
    //     anything but the device asked for.
    //
    // `matchPinnedDevice` is the same cascade a stored pin is matched by - groupId, which the
    // aggregate shares with the device behind it, then the label it carries behind its
    // "Default - " prefix. Falling back to the aggregate keeps the previous behaviour when
    // neither identifies anything.
    return (
      matchPinnedDevice(allDevices, 'audioinput', aggregate)?.device ||
      aggregate
    );
  }, [pins, phoneDevice, osDefaults, allDevices]);
  const microphoneId = useMemo(() => selectedMicrophone?.deviceId, [
    selectedMicrophone,
  ]);
  // Its NAME, which is the only part of it that moves when the OS default does: an unpinned role
  // resolves onto the aggregate, whose deviceId is the constant 'default' for every device it may
  // ever point at. Keying the preview on the id alone meant the effect never re-ran and the meter
  // kept reading whichever microphone was default when it opened, while the dropdown right above
  // it correctly renamed itself to the new one.
  const microphoneLabel = useMemo(() => selectedMicrophone?.label, [
    selectedMicrophone,
  ]);
  // The explicitly chosen ring device, but only while it is still attached.
  const ringDeviceId = useMemo(
    () =>
      ringDevice?.deviceId &&
      speakers.some(e => e.deviceId === ringDevice.deviceId)
        ? ringDevice.deviceId
        : undefined,
    [ringDevice, speakers]
  );

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  const getAvailableDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (devices) {
        // Real devices only. Chromium's virtual "Default - X" aggregates are represented by the
        // explicit "System default" option below, which works on every browser.
        setAllDevices(devices);
        setMicrophones(listRealAudioDevices(devices, 'audioinput'));
        setSpeakers(listRealAudioDevices(devices, 'audiooutput'));
        setOsDefaults({
          microphone: getSystemDefaultDevice(devices, 'audioinput'),
          speaker: getSystemDefaultDevice(devices, 'audiooutput'),
        });
      }
    } catch (err) {
      log.error('Error to access available media devices:', err);
    }
  }, []);

  function stopMicrophone() {
    audio?.getTracks().forEach(track => track.stop());
    setAudio(null);
  }

  useEffect(() => {
    getAvailableDevices();

    const media = navigator.mediaDevices;

    if (!media?.addEventListener) {
      return undefined;
    }

    // List-only refresh. This listener never writes to redux - there is exactly one
    // devicechange listener that does, and it lives in screens/Home.
    media.addEventListener('devicechange', getAvailableDevices);

    return () => media.removeEventListener('devicechange', getAvailableDevices);
  }, [getAvailableDevices]);

  // The live level meter's preview stream. Deliberately keyed on the resolved microphone id
  // alone and not on `phoneDevice`: this component is also mounted in-call through the
  // HeadsetManager dialog, and re-opening the preview on every unrelated deviceManager write
  // would grab the microphone during a call. The cleanup releases the previous device, which the
  // old effect never did when the selection changed.
  useEffect(() => {
    if (!microphoneId) {
      return undefined;
    }

    let stream;
    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({
        audio: { deviceId: { exact: microphoneId } },
        video: false,
      })
      .then(value => {
        if (cancelled) {
          value.getTracks().forEach(track => track.stop());
          return;
        }

        stream = value;
        setAudio(value);
      })
      .catch(err => log.error('Error to access microphone devices: ', err));

    return () => {
      cancelled = true;
      stream?.getTracks().forEach(track => track.stop());
      setAudio(null);
    };
  }, [microphoneId, microphoneLabel]);

  /**
   * Apply an explicit device pick. `device` is null for the "System default" option, which
   * unpins the role so it follows the OS default again.
   */
  function changeDevice(device, type) {
    // Pins the role, re-resolves against the live hardware, and moves any call already in
    // progress onto the new device.
    dispatch(
      actions.setAudioDevicePreference(type, toDeviceInfo(device) || null)
    );

    lib.ToastMessage('success', messagesToast().SYSTEM[type].change.success);
  }

  /**
   * The value for one of the audio selects: the pinned deviceId while that pin is the device
   * actually IN USE, otherwise the "System default" sentinel - whose label names the device that
   * is in use instead.
   *
   * "In use" rather than "attached", so a pinned device that is plugged in but did not win -
   * busy, or the mid-call move onto it failed - is not shown as the one being spoken into.
   */
  function selectedValue(kind, options) {
    const pinnedId = pins?.[kind]?.deviceId;

    return isPinInUse(pins?.[kind], phoneDevice?.audio?.[kind]) &&
      options?.some(e => e.deviceId === pinnedId)
      ? pinnedId
      : SYSTEM_DEFAULT;
  }

  /** Resolve a select's change event back to a device object, or null for "System default". */
  function optionByValue(options, value) {
    return value === SYSTEM_DEFAULT
      ? null
      : options.find(e => e.deviceId === value);
  }

  /**
   * Label for the "System default" option, naming the device that option would actually select.
   *
   * That is what the OS considers default, so it must come from the live enumeration rather than
   * from `deviceManager.audio.*`. The two agree only while the role is unpinned: pinning a device
   * makes it the resolved one, and labelling this row from that turned the row into "System
   * default (the device I just picked)" - it renamed itself to whatever was selected instead of
   * naming the option's own destination.
   *
   * The ring device is the exception and deliberately passes the resolved SPEAKER: an unset ring
   * device means "ring wherever calls play", so its "System default" really does follow the call
   * speaker, pin included.
   */
  function systemDefaultLabel(device) {
    const label = t(
      'screens.configuration.components.deviceManager.systemDefault'
    );
    const name = stripDefaultPrefix(device?.label);

    return name ? `${label} (${name})` : label;
  }

  function volumeHandler(event, value) {
    if (value >= 0.2) {
      setVolumeValue(value);
      dispatch(actions.volumeControl(value));
    }
  }

  return (
    <BoxDevices>
      <ListDevices>
        <div className="title">
          <div />
          <span>
            {t('screens.configuration.components.deviceManager.microphone')}
          </span>
          <div />
        </div>
        <BoxSelect>
          <Select
            className="inputSelect"
            value={selectedValue('microphone', microphones)}
            onChange={value =>
              changeDevice(
                optionByValue(microphones, value.target.value),
                'microphone'
              )
            }>
            <MenuItem value={SYSTEM_DEFAULT}>
              {systemDefaultLabel(osDefaults.microphone)}
            </MenuItem>
            {microphones?.map(e => (
              <MenuItem key={e.deviceId} value={e.deviceId}>
                {stripDefaultPrefix(e.label)}
              </MenuItem>
            ))}
          </Select>

          {audio && (
            <NuacomComponent.MicrophoneAudioAnalyzer
              dashes
              stopMicrophone={stopMicrophone}
              audio={audio}
            />
          )}
        </BoxSelect>
      </ListDevices>

      <ListDevices>
        <div className="title">
          <div />
          <span>
            {t('screens.configuration.components.deviceManager.speaker')}
          </span>
          <div />
        </div>
        <BoxSelect>
          <Select
            className="inputSelect"
            value={selectedValue('speaker', speakers)}
            onChange={value =>
              changeDevice(
                optionByValue(speakers, value.target.value),
                'speaker'
              )
            }>
            <MenuItem value={SYSTEM_DEFAULT}>
              {systemDefaultLabel(osDefaults.speaker)}
            </MenuItem>
            {speakers?.map(e => (
              <MenuItem key={e.deviceId} value={e.deviceId}>
                {stripDefaultPrefix(e.label)}
              </MenuItem>
            ))}
          </Select>
          {showSpeakerVolume && (
            <VolumeComponent>
              <VolumeUp style={{ color: '#6c6c6c', marginRight: 10 }} />
              <Slider
                style={{ marginRight: 5 }}
                defaultValue={0.5}
                min={0.1}
                max={1}
                step={0.1}
                value={volumeValue}
                onChange={volumeHandler}
              />
            </VolumeComponent>
          )}
        </BoxSelect>
        {!disableAudioTest && (
          <NuacomComponent.PlayTest
            speaker
            deviceId={phoneDevice?.audio?.speaker?.deviceId}
          />
        )}
      </ListDevices>

      {!disableRingDevice && (
        <ListDevices style={{ marginTop: 0 }}>
          <div className="title">
            <div />
            <span>
              {t('screens.configuration.components.deviceManager.ringDevice')}
            </span>
            <div />
          </div>
          <BoxSelect>
            <Select
              className="inputSelect"
              value={ringDeviceId || SYSTEM_DEFAULT}
              onChange={value =>
                changeDevice(
                  optionByValue(speakers, value.target.value),
                  'ringDevice'
                )
              }>
              {/* Unset, the ring device follows the call speaker. */}
              <MenuItem value={SYSTEM_DEFAULT}>
                {systemDefaultLabel(phoneDevice?.audio?.speaker)}
              </MenuItem>
              {speakers?.map(e => (
                <MenuItem key={e.deviceId} value={e.deviceId}>
                  {stripDefaultPrefix(e.label)}
                </MenuItem>
              ))}
            </Select>
          </BoxSelect>

          {/* The resolved ringer, which already falls back to the speaker when the chosen
              ring device is unset or unplugged. */}
          <NuacomComponent.PlayTest
            deviceId={phoneDevice?.audio?.ringer?.deviceId}
          />
        </ListDevices>
      )}
    </BoxDevices>
  );
}

DeviceManager.defaultProps = {
  disableRingDevice: false,
  disableAudioTest: false,
  showSpeakerVolume: false,
};

DeviceManager.propTypes = {
  disableRingDevice: lib.PropTypes.bool,
  disableAudioTest: lib.PropTypes.bool,
  showSpeakerVolume: lib.PropTypes.bool,
};
